import React, { useState, useEffect, useRef, useLayoutEffect } from "react";

// --- CUSTOM HOOKS ---
// --- CUSTOM HOOKS ---
const useTypewriter = (phrases: string[], shouldAnimate: boolean, videoRef?: React.RefObject<HTMLVideoElement> | React.RefObject<HTMLVideoElement | null>) => {
  const [placeholder, setPlaceholder] = useState("");
  // Legacy state for fallback
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Constants
  const TYPE_SPEED = 45;
  const DELETE_SPEED = 20;
  const PAUSE_DURATION = 1682; // Synced to 17.567s video loop

  // Pre-calculate timeline for video sync
  // We memoize this to avoid recalculating on every render
  const timeline = React.useMemo(() => {
    let currentTime = 0;
    return phrases.map(phrase => {
      const typeDuration = phrase.length * TYPE_SPEED;
      const pauseDuration = PAUSE_DURATION;
      const deleteDuration = phrase.length * DELETE_SPEED;
      const totalDuration = typeDuration + pauseDuration + deleteDuration;

      const start = currentTime;
      const end = start + totalDuration;
      currentTime = end;

      return {
        phrase,
        start,
        end,
        typeDuration,
        pauseDuration
      };
    });
  }, [phrases]);

  const totalCycleDuration = timeline[timeline.length - 1].end;

  // VIDEO SYNC EFFECT
  useEffect(() => {
    // If no video ref, skip this effect and use the fallback
    if (!videoRef?.current || !shouldAnimate) return;

    let animationFrameId: number;

    const updateText = () => {
      if (!videoRef.current) return;

      // Get video time in MS
      const videoTimeMs = videoRef.current.currentTime * 1000;

      // Handle looping: Use modulo of the total cycle duration
      // This ensures that even if video duration slightly mismatched, we cycle correctly
      // matching the video's loop.
      const timeInCycle = videoTimeMs % totalCycleDuration;

      // Find which phrase we are in
      const activeItem = timeline.find(item => timeInCycle >= item.start && timeInCycle < item.end);

      if (activeItem) {
        const localTime = timeInCycle - activeItem.start;
        let newText = "";

        if (localTime < activeItem.typeDuration) {
          // TYPING: Calculate how many chars to show
          // Using Math.ceil ensuring at least 1 char is shown immediately if slight buffer
          const charCount = Math.min(Math.ceil(localTime / TYPE_SPEED), activeItem.phrase.length);
          newText = activeItem.phrase.substring(0, charCount);
        } else if (localTime < activeItem.typeDuration + activeItem.pauseDuration) {
          // PAUSED: Show full text
          newText = activeItem.phrase;
        } else {
          // DELETING: Calculate how many chars to remove
          const timeInDelete = localTime - (activeItem.typeDuration + activeItem.pauseDuration);
          const charsToDelete = Math.floor(timeInDelete / DELETE_SPEED);
          // Ensure we don't delete more than length
          const len = Math.max(0, activeItem.phrase.length - charsToDelete);
          newText = activeItem.phrase.substring(0, len);
        }

        setPlaceholder(newText);
      }

      animationFrameId = requestAnimationFrame(updateText);
    };

    // Start loop
    animationFrameId = requestAnimationFrame(updateText);

    return () => cancelAnimationFrame(animationFrameId);
  }, [videoRef, shouldAnimate, timeline, totalCycleDuration]);

  // FALLBACK EFFECT (Original Logic)
  // Only runs if videoRef is NOT provided
  useEffect(() => {
    if (videoRef?.current || !shouldAnimate) return;

    // ALWAYS RUNNING - NO INTERRUPTIONS
    const currentPhrase = phrases[phraseIndex];

    const handleTyping = () => {
      if (isDeleting) {
        const next = currentPhrase.substring(0, placeholder.length - 1);
        setPlaceholder(next);
        if (next === "") {
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      } else {
        const next = currentPhrase.substring(0, placeholder.length + 1);
        setPlaceholder(next);
        if (next === currentPhrase) {
        }
      }
    };

    if (!isDeleting && placeholder === currentPhrase) {
      const pauseTimer = setTimeout(() => setIsDeleting(true), PAUSE_DURATION);
      return () => clearTimeout(pauseTimer);
    }

    const timer = setTimeout(handleTyping, isDeleting ? DELETE_SPEED : TYPE_SPEED);
    return () => clearTimeout(timer);
  }, [placeholder, isDeleting, phraseIndex, phrases, PAUSE_DURATION, DELETE_SPEED, TYPE_SPEED, shouldAnimate, videoRef]);

  return placeholder;
};

interface SpatialInputProps {
  className?: string;
  onSubmit?: (value: string) => void;
  shouldAnimate?: boolean;
  videoRef?: React.RefObject<HTMLVideoElement> | React.RefObject<HTMLVideoElement | null>;
}

const SpatialInput: React.FC<SpatialInputProps> = ({ className = "", onSubmit, shouldAnimate = true, videoRef }) => {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  // Height State - Start larger due to bigger font
  const [boxHeight, setBoxHeight] = useState(100);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const lastValidValue = useRef(""); // Track last state that fit within 5 lines
  const containerRef = useRef<HTMLDivElement>(null);

  const BASE_HEIGHT = 100;
  const MAX_HEIGHT = 256;


  const placeholderPhrases = [
    "86 exhibitor fair",
    "300 booth trade show",
    "Concert for 3k fans",
    "650 seat graduation",
    "Outdoor food festival",
    "Describe your event",
  ];


  const placeholderText = useTypewriter(placeholderPhrases, shouldAnimate, videoRef);

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // --- HEIGHT CALCULATION & SCROLL LOCK ---
  useLayoutEffect(() => {
    if (ghostRef.current) {
      // Only measure height based on INPUT value, not placeholder.
      // If input is empty, we default to base height.
      if (!inputValue) {
        setBoxHeight(BASE_HEIGHT);
        return;
      }

      // Use ghostRef to measure height. It already contains inputValue (see render)
      const scrollHeight = ghostRef.current.scrollHeight;

      // If we are over the limit during manual typing, revert to the last valid value
      if (scrollHeight > 192) {
        setInputValue(lastValidValue.current);
        return;
      }

      // Update last valid value if we're within bounds
      lastValidValue.current = inputValue;

      // Always calculate full height based on ghost element
      // Add generous padding (64px) to ensure breathing room for cursor
      const totalHeight = scrollHeight + 64;

      if (totalHeight > MAX_HEIGHT) {
        setBoxHeight(MAX_HEIGHT);
      } else {
        // Ensure minimum height constraint
        setBoxHeight(Math.max(totalHeight, BASE_HEIGHT));
      }
    }

    // FORCE SCROLL TO TOP to prevent jumping
    if (inputRef.current) {
      inputRef.current.scrollTop = 0;
    }
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [inputValue]); // REMOVED placeholderText dependency to fix bounce

  return (
    <div className={`relative w-full max-w-[960px] group z-20 ${className}`}>
      <div
        ref={containerRef}
        className={`
                relative flex items-center justify-between w-full px-5 md:px-8 bg-white overflow-hidden
                border-2 transform hover:scale-[1.01] origin-top
                transition-[border,box-shadow,transform] duration-200 ease-out
                ${isFocused
            ? "border-[#0055FF] shadow-[8px_8px_0px_0px_#0055FF]"
            : "border-[#1A1A1A] shadow-[8px_8px_0px_0px_#E0E0E0] hover:shadow-[8px_8px_0px_0px_#1A1A1A]"
          }
              `}
        style={{ height: `${boxHeight}px` }}
      >
        {/* Padded py-8 for larger text breathing room */}
        <div
          className="relative flex-1 flex items-start py-8 mr-6"
          style={{ height: `${boxHeight}px` }}
        >

          {/* 1. ACTUAL INPUT */}
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            onScroll={(e) => {
              e.currentTarget.scrollTop = 0;
            }}
            rows={1}
            aria-label="Describe your event"
            // TEXT SCALED UP: text-[24px] mobile, text-[32px] desktop
            className="w-full h-full bg-transparent text-[24px] md:text-[32px] font-medium text-[#111111] placeholder:text-transparent outline-none font-mono tracking-tight resize-none leading-[1] !overflow-hidden whitespace-pre-wrap break-words p-0 m-0"
            style={{ caretColor: '#0055FF' }}
          />

          {/* 2. GHOST ELEMENT - Added pr-4 to force slightly earlier wrap than textarea (pr-2) */}
          <div
            ref={ghostRef}
            className="absolute top-0 left-0 w-full invisible pointer-events-none text-[24px] md:text-[32px] font-medium font-mono tracking-tight leading-[1.2] whitespace-pre-wrap break-words text-left pr-4"
            aria-hidden="true"
          >
            {/* Append dot if ending in newline to strictly force height calculation */}
            {inputValue || placeholderText}{inputValue.endsWith('\n') ? '.' : ''}
          </div>

          {/* 3. TYPEWRITER OVERLAY */}
          {!inputValue && !isFocused && (
            <div className="absolute inset-0 flex items-start pt-8 pointer-events-none select-none">
              <span className="text-[24px] md:text-[32px] text-[#1A1A1A]/60 font-medium font-mono tracking-tight leading-[1.2] text-left">
                {placeholderText}<span className="inline-block w-0 h-[1em] relative align-text-bottom"><span className="absolute left-1 bottom-[2px] w-[3px] h-[1em] bg-[#0055FF]"></span></span>
              </span>
            </div>
          )}
        </div>

        <div className="w-[2px] bg-[#E0E0E0] hidden md:block self-stretch my-8"></div>

        <button
          onClick={handleSubmit}
          // Removed manual margins, relying on flex items-center
          // BUTTON UPDATED: Blue default, shadow on hover
          className="md:ml-8 flex items-center justify-center px-2 md:px-8 h-12 md:h-16 bg-[#0055FF] text-white hover:shadow-[4px_4px_0px_0px_#1A1A1A] transition-all duration-200 shrink-0"
        >
          <span className="font-mono text-[12px] md:text-base font-bold tracking-tight uppercase">
            Get Started
          </span>
        </button>

        {/* CORNER ACCENTS - Scaled slightly */}
        <div className="absolute top-1 left-1 w-2 h-3 border-t-2 border-l-2 border-[#1A1A1A] opacity-20 group-hover:opacity-100 transition-opacity"></div>
        <div className="absolute top-1 right-1 w-2 h-3 border-t-2 border-r-2 border-[#1A1A1A] opacity-20 group-hover:opacity-100 transition-opacity"></div>
        <div className="absolute bottom-1 left-1 w-2 h-3 border-b-2 border-l-2 border-[#1A1A1A] opacity-20 group-hover:opacity-100 transition-opacity"></div>
        <div className="absolute bottom-1 right-1 w-2 h-3 border-b-2 border-r-2 border-[#1A1A1A] opacity-20 group-hover:opacity-100 transition-opacity"></div>
      </div>
    </div>
  );
};

export default SpatialInput;
